import type { ActivityLog } from './ActivityLog';
import type { AssetVerificationSetting } from './Settings';

export enum ScreeningDataProvider {
    Yodlee = 'yodlee',
    Plaid = 'plaid',
    Mx = 'mx',
    Flinks = 'flinks',
    Fiserv = 'fiserv',
}

export interface Applicant {
    applicantFirstName: string;
    applicantLastName: string;
    applicantEmail: string;
    applicantPhoneNumber: string | null;
    screeningId?: string;
    screeningStatus?: ScreeningStatus;
}

export enum ScreeningStatus {
    NotStarted = 'not_started',
    InProgress = 'in_progress',
    Completed = 'completed',
    // Pending exists only while screening_status mirrors the verification status -
    // remove with https://payscore.atlassian.net/browse/PAY-2105
    Pending = 'pending',
}

export enum ReasonCompleted {
    NotCompleted = 'not_completed',
    Finished = 'finished',
    MissingBank = 'missing_bank',
    ShareBank = 'share_bank',
    NoIncome = 'no_income',
    NoBank = 'no_bank',
    BankCannotConnect = 'bank_cannot_connect',
    NoOnlineBanking = 'no_online_banking',
}

export interface Screening {
    id: string;
    applicantFirstName: string;
    applicantLastName: string;
    applicantEmail: string;
    applicantPhoneNumber: string | null;
    screeningStatus: ScreeningStatus;
    reasonCompleted: ReasonCompleted;
}

export interface ScreeningDetails extends Screening {
    propertyName: string;
    createdAtTimestamp: number;
}

export enum ApprovalRecommendation {
    Unavailable = 'unavailable',
    Above = 'above',
    Below = 'below',
    Equal = 'equal',
}

export interface ScreeningGroup {
    approvalRecommendation: ApprovalRecommendation | null;
    assetVerificationReportCompleted: boolean;
    correlationId: string | null;
    correlationId2: string | null;
    createdAtTimestamp: number;
    decisionMakerDisplayName: string | null;
    id: string;
    incomeMultiplierThreshold: number | null;
    isDecisionMakerPaying: boolean;
    isExpired: boolean;
    isReportExpired: boolean;
    lastInvitationSentTimestamp: number | null;
    monthlyRentCents: number | null;
    numberOfInvitationsSent: number | null;
    propertyName: string | null;
    propertyStreetAddress: string | null;
    reportViewedAt: number | null;
    screenings: Screening[];
    status: ScreeningStatus;
    unitNumber: string | null;
}

export interface ScreeningGroupDetails extends ScreeningGroup {
    activityLogs: ActivityLog[];
    assetVerification: AssetVerificationSetting;
    disableNotificationEmails: boolean;
    expiresAtTimestamp: number | null;
    forceCompletedUser: ReasonForceCompletedUser | null;
    isFree: boolean;
    linkWorkflow: boolean;
    propertyId: string | null;
    reasonForceCompleted: string | null;
    unitId: string | null;
    userFullName: string | null;
}

export enum CorrelationSource {
    Appfolio = 'appfolio',
    DecisionLender = 'decision_lender',
    Entrata = 'entrata',
    Fortress = 'fortress',
    Hemlane = 'hemlane',
    ILoveLeasing = 'i_love_leasing',
    Mri = 'mri',
    NewOneSite = 'new_one_site',
    OneSite = 'one_site',
    Onsite = 'onsite',
    Propertyware = 'propertyware',
    RentalHistoryReports = 'rental_history_reports',
    RentManager = 'rent_manager',
    RentScreener = 'rent_screener',
    RentVine = 'rent_vine',
    Resman = 'resman',
    TazWorks = 'tazworks',
    Yardi = 'yardi',
    YardiBreeze = 'yardi_breeze',
    YardiIQ = 'yardi_iq',
    YardiRentCafe = 'yardi_rent_cafe',
}

export enum VerificationType {
    Income = 'income',
    Assets = 'assets',
}

export interface InjectedScreeningGroup {
    applicants: Applicant[];
    propertyId: string | null;
    unitId: string | null;
    correlationId: string | undefined;
    monthlyRentCents: number | null;
    correlationSource: CorrelationSource | null;
    decisionMakerDisplayName: string | undefined;
}

export interface ScreeningThatShareBank {
    id: string;
    responsibleToConnectSharedBank: boolean | null;
}

export interface ReasonForceCompletedUser {
    firstName: string;
    lastName: string;
}

